import { useState, useEffect } from 'react';
import { X, Mail, Lock, LogIn, UserPlus, Eye, EyeOff } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useAuth } from '../providers/AuthProvider';
import { useCreateUserProfile } from '../hooks';
import { auth } from '../api/firebaseConfig.ts';
import classNames from 'classnames';
import { onAuthStateChanged, sendPasswordResetEmail } from 'firebase/auth';

// Interfaces for form data
interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface LoginFormData {
  email: string;
  password: string;
}

interface SignupFormData {
  email: string;
  password: string;
  confirmPassword: string;
  termsAccepted: boolean;
}

// Auth modal component
export function AuthModal({ isOpen, onClose }: AuthModalProps) {
  // State
  const [activeTab, setActiveTab] = useState<'login' | 'signup'>('login');
  const [resetSuccess, setResetSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState({
    login: false,
    signup: false,
    confirm: false,
  });
  const { formatMessage } = useIntl();

  // Auth and profile hooks
  const { signInWithGoogle, signInWithEmail, signUpWithEmail, loading } =
    useAuth();
  const { mutate: addUser, isPending } = useCreateUserProfile();

  // Effect to track auth state
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        addUser(user.uid);
      }
    });

    return () => unsubscribe();
  }, [addUser]);

  // Effect to handle Escape key press
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Form initialization
  const loginForm = useForm<LoginFormData>({
    mode: 'onChange',
  });
  const signupForm = useForm<SignupFormData>({
    mode: 'onChange',
    defaultValues: {
      termsAccepted: false,
    },
  });

  const {
    formState: { isValid },
    watch,
  } = signupForm;

  // Password validation rules
  const passwordValidation = {
    required: formatMessage({
      id: 'auth.passwordRequired',
      defaultMessage: 'Пароль обязателен',
    }),
    minLength: {
      value: 8,
      message: formatMessage({
        id: 'auth.passwordMinLength',
        defaultMessage: 'Пароль должен содержать минимум 8 символов',
      }),
    },
    maxLength: {
      value: 128,
      message: formatMessage({
        id: 'auth.passwordMaxLength',
        defaultMessage: 'Пароль не должен превышать 128 символов',
      }),
    },
    pattern: {
      value: /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d@$!%*?&]{8,128}$/,
      message: formatMessage({
        id: 'auth.passwordInvalid',
        defaultMessage:
          'Пароль должен содержать хотя бы одну латинскую букву и одну цифру',
      }),
    },
  };

  // Watch password fields for real-time validation
  const password = watch('password');

  // If the modal is closed, render nothing
  if (!isOpen) return null;

  // Form handlers
  const handleLogin = loginForm.handleSubmit(async (data) => {
    try {
      await signInWithEmail(data.email, data.password);
      onClose();
    } catch (error) {
      setError(
        formatMessage({
          id: 'auth.errorSigning',
          defaultMessage: 'Ошибка авторизации. Проверьте логин и пароль',
        }),
      );
    }
  });

  const handleSignup = signupForm.handleSubmit(async (data) => {
    try {
      await signUpWithEmail(data.email, data.password);
      onClose();
    } catch (error) {
      setError(
        formatMessage({
          id: 'auth.errorSigningEmailIsAlreadyExists',
          defaultMessage:
            'Ошибка авторизации. Возможно пользователь уже существует',
        }),
      );
    }
  });

  const handleGoogleAuth = async () => {
    try {
      await signInWithGoogle();
      onClose();
    } catch (error) {
      setError(
        formatMessage({
          id: 'auth.errorSigningWithGoogle',
          defaultMessage: 'Ошибка авторизации через Google',
        }),
      );
    }
  };

  const handlePasswordReset = async () => {
    const email = loginForm.getValues('email');
    if (!email) {
      setError(
        formatMessage({
          id: 'auth.provideEmailForResetPassword',
          defaultMessage: 'Пожалуйста, введите email для восстановления пароля',
        }),
      );
      return;
    }

    try {
      await sendPasswordResetEmail(auth, email);
      setResetSuccess(
        formatMessage({
          id: 'auth.resetEmailIsSend',
          defaultMessage:
            'Письмо для восстановления пароля отправлено на ваш email',
        }),
      );
      setError(null);
      setTimeout(() => {
        setResetSuccess(null);
      }, 5000);
    } catch (error) {
      setError(
        formatMessage({
          id: 'auth.resetEmailIsError',
          defaultMessage:
            'Ошибка при отправке письма для восстановления пароля',
        }),
      );
    }
  };

  // Toggle password visibility
  const togglePasswordVisibility = (field: keyof typeof showPassword) => {
    setShowPassword((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  // Modal rendering
  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center'>
      {/* Background */}
      <div className='absolute inset-0 bg-black/50' onClick={onClose} />

      {/* Modal container */}
      <div className='relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4'>
        {/* Tabs */}
        <div className='flex border-b border-gray-200'>
          <button
            className={`flex-1 py-4 text-center font-medium ${
              activeTab === 'login'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => {
              setError(null);
              setActiveTab('login');
            }}
          >
            <LogIn className='w-5 h-5 inline-block mr-2' />
            <FormattedMessage id='auth.login' defaultMessage='Войти' />
          </button>
          <button
            className={`flex-1 py-4 text-center font-medium ${
              activeTab === 'signup'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => {
              setError(null);
              setActiveTab('signup');
            }}
          >
            <UserPlus className='w-5 h-5 inline-block mr-2' />
            <FormattedMessage
              id='auth.signup'
              defaultMessage='Зарегистрироваться'
            />
          </button>
          {/* Close button */}
          <button
            onClick={onClose}
            className='text-gray-400 hover:text-gray-600 z-10 px-4'
          >
            <X className='w-5 h-5' />
          </button>
        </div>

        {/* Modal content */}
        <div className='p-6'>
          {/* Error message */}
          {error && (
            <div className='mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm'>
              {error}
            </div>
          )}
          {/* Success message */}
          {resetSuccess && (
            <div className='mb-4 p-3 bg-green-50 text-green-700 rounded-lg text-sm'>
              {resetSuccess}
            </div>
          )}

          {/* Google auth button */}
          <button
            onClick={handleGoogleAuth}
            className='w-full flex items-center justify-center gap-2 bg-white border border-gray-300 text-gray-700 px-4 py-2.5 rounded-lg hover:bg-gray-50 mb-6'
          >
            <img
              src='https://www.google.com/favicon.ico'
              alt='Google'
              className='w-5 h-5'
            />
            <FormattedMessage
              id='auth.continueWithGoogle'
              defaultMessage='Продолжить с Google'
            />
          </button>

          {/* "Or" divider */}
          <div className='relative mb-6'>
            <div className='absolute inset-0 flex items-center'>
              <div className='w-full border-t border-gray-200' />
            </div>
            <div className='relative flex justify-center text-sm'>
              <span className='px-2 bg-white text-gray-500'>
                <FormattedMessage id='auth.or' defaultMessage='или' />
              </span>
            </div>
          </div>

          {/* Login form */}
          {activeTab === 'login' ? (
            <form onSubmit={handleLogin} className='space-y-4'>
              {/* Email field */}
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1'>
                  <FormattedMessage
                    id='auth.email'
                    defaultMessage='Электронная почта'
                  />
                  <span className='text-red-500'>*</span>
                </label>
                <div className='relative'>
                  <Mail className='absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5' />
                  <input
                    type='email'
                    {...loginForm.register('email', {
                      required: formatMessage({
                        id: 'auth.emailRequired',
                        defaultMessage: 'Электронная почта обязательна',
                      }),
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: formatMessage({
                          id: 'auth.emailInvalid',
                          defaultMessage: 'Неверный формат электронной почты',
                        }),
                      },
                    })}
                    className='text-gray-900 w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                    placeholder='your@email.com'
                  />
                </div>
                {loginForm.formState.errors.email && (
                  <p className='mt-1 text-sm text-red-600'>
                    {loginForm.formState.errors.email.message}
                  </p>
                )}
              </div>

              {/* Password field */}
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1'>
                  <FormattedMessage
                    id='auth.password'
                    defaultMessage='Пароль'
                  />
                  <span className='text-red-500'>*</span>
                </label>
                <div className='relative'>
                  <Lock className='absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5' />
                  <input
                    type={showPassword.login ? 'text' : 'password'}
                    {...loginForm.register('password', passwordValidation)}
                    className='text-gray-900 w-full pl-10 pr-12 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                    placeholder='••••••••'
                  />
                  <button
                    type='button'
                    onClick={() => togglePasswordVisibility('login')}
                    className='absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600'
                  >
                    {showPassword.login ? (
                      <EyeOff className='w-5 h-5' />
                    ) : (
                      <Eye className='w-5 h-5' />
                    )}
                  </button>
                </div>
                {loginForm.formState.errors.password && (
                  <p className='mt-1 text-sm text-red-600'>
                    {loginForm.formState.errors.password.message}
                  </p>
                )}
              </div>

              {/* Forgot Password Link */}
              <div className='text-right'>
                <button
                  type='button'
                  onClick={handlePasswordReset}
                  className='text-sm text-blue-600 hover:underline'
                >
                  <FormattedMessage
                    id='auth.forgotPassword'
                    defaultMessage='Восстановить пароль'
                  />
                </button>
              </div>

              {/* Login button */}
              <button
                type='submit'
                className='w-full bg-blue-600 text-white py-2.5 rounded-lg hover:bg-blue-700'
              >
                <FormattedMessage
                  id='auth.loginButton'
                  defaultMessage='Войти'
                />
              </button>
            </form>
          ) : (
            /* Signup form */
            <form onSubmit={handleSignup} className='space-y-4'>
              {/* Email field */}
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1'>
                  <FormattedMessage
                    id='auth.email'
                    defaultMessage='Электронная почта'
                  />
                  <span className='text-red-500'>*</span>
                </label>
                <div className='relative'>
                  <Mail className='absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5' />
                  <input
                    type='email'
                    {...signupForm.register('email', {
                      required: formatMessage({
                        id: 'auth.emailRequired',
                        defaultMessage: 'Электронная почта обязательна',
                      }),
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: formatMessage({
                          id: 'auth.emailInvalid',
                          defaultMessage: 'Неверный формат электронной почты',
                        }),
                      },
                    })}
                    className='text-gray-900 w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                    placeholder='your@email.com'
                  />
                </div>
                {signupForm.formState.errors.email && (
                  <p className='mt-1 text-sm text-red-600'>
                    {signupForm.formState.errors.email.message}
                  </p>
                )}
              </div>

              {/* Password field */}
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1'>
                  <FormattedMessage
                    id='auth.password'
                    defaultMessage='Пароль'
                  />
                  <span className='text-red-500'>*</span>
                </label>
                <div className='relative'>
                  <Lock className='absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5' />
                  <input
                    type={showPassword.signup ? 'text' : 'password'}
                    {...signupForm.register('password', passwordValidation)}
                    className='text-gray-900 w-full pl-10 pr-12 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                    placeholder='••••••••'
                  />
                  <button
                    type='button'
                    onClick={() => togglePasswordVisibility('signup')}
                    className='absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600'
                  >
                    {showPassword.signup ? (
                      <EyeOff className='w-5 h-5' />
                    ) : (
                      <Eye className='w-5 h-5' />
                    )}
                  </button>
                </div>
                {signupForm.formState.errors.password && (
                  <p className='mt-1 text-sm text-red-600'>
                    {signupForm.formState.errors.password.message}
                  </p>
                )}
                <ul className='mt-2 text-sm text-gray-600'>
                  <li>
                    <FormattedMessage
                      id='auth.passwordRuleLength'
                      defaultMessage='От 8 до 128 символов'
                    />
                  </li>
                  <li>
                    <FormattedMessage
                      id='auth.passwordRuleLetter'
                      defaultMessage='Хотя бы одна латинская буква'
                    />
                  </li>
                  <li>
                    <FormattedMessage
                      id='auth.passwordRuleNumber'
                      defaultMessage='Хотя бы одна цифра'
                    />
                  </li>
                  <li>
                    <FormattedMessage
                      id='auth.passwordRuleSpecial'
                      defaultMessage='Допустимы специальные символы (@$!%*?&)'
                    />
                  </li>
                </ul>
              </div>

              {/* Confirm password field */}
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1'>
                  <FormattedMessage
                    id='auth.confirmPassword'
                    defaultMessage='Подтвердите пароль'
                  />
                  <span className='text-red-500'>*</span>
                </label>
                <div className='relative'>
                  <Lock className='absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5' />
                  <input
                    type={showPassword.confirm ? 'text' : 'password'}
                    {...signupForm.register('confirmPassword', {
                      required: formatMessage({
                        id: 'auth.confirmPasswordRequired',
                        defaultMessage: 'Подтверждение пароля обязательно',
                      }),
                      validate: (value) =>
                        value === password ||
                        formatMessage({
                          id: 'auth.passwordsDoNotMatch',
                          defaultMessage: 'Пароли не совпадают',
                        }),
                    })}
                    className='text-gray-900 w-full pl-10 pr-12 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                    placeholder='••••••••'
                  />
                  <button
                    type='button'
                    onClick={() => togglePasswordVisibility('confirm')}
                    className='absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600'
                  >
                    {showPassword.confirm ? (
                      <EyeOff className='w-5 h-5' />
                    ) : (
                      <Eye className='w-5 h-5' />
                    )}
                  </button>
                </div>
                {signupForm.formState.errors.confirmPassword && (
                  <p className='mt-1 text-sm text-red-600'>
                    {signupForm.formState.errors.confirmPassword.message}
                  </p>
                )}
              </div>

              {/* Terms and Conditions checkbox */}
              <div>
                <label className='flex items-center space-x-2 cursor-pointer'>
                  <input
                    type='checkbox'
                    {...signupForm.register('termsAccepted', {
                      required: formatMessage({
                        id: 'auth.termsRequired',
                        defaultMessage:
                          'Вы должны согласиться с условиями использования',
                      }),
                    })}
                    className='h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500'
                  />
                  <span className='text-sm text-gray-600'>
                    <FormattedMessage
                      id='auth.termsCheckbox'
                      defaultMessage='Регистрируясь, я соглашаюсь с'
                    />{' '}
                    <a
                      href='/terms-and-conditions'
                      target='_blank'
                      rel='noopener noreferrer'
                      className='text-blue-600 hover:underline'
                    >
                      <FormattedMessage
                        id='auth.termsLink'
                        defaultMessage='Условиями использования'
                      />
                    </a>
                    <span className='text-red-500'>*</span>
                  </span>
                </label>
                {signupForm.formState.errors.termsAccepted && (
                  <p className='mt-1 text-sm text-red-600'>
                    {signupForm.formState.errors.termsAccepted.message}
                  </p>
                )}
              </div>

              {/* Signup button */}
              <button
                type='submit'
                disabled={!isValid || isPending || loading}
                className={classNames('w-full py-2.5 rounded-lg text-white', {
                  'bg-gray-400 cursor-not-allowed':
                    !isValid || isPending || loading,
                  'bg-blue-600 hover:bg-blue-700':
                    isValid && !isPending && !loading,
                })}
              >
                <FormattedMessage
                  id='auth.signupButton'
                  defaultMessage='Зарегистрироваться'
                />
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
